import { Tooltip } from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import capitalize from 'lodash/capitalize';
import { useEffect, useState } from 'react';
import { PRESET_COLORS } from '~/constants/color';
import { useClickAwayListener } from '~/hooks/useClickAwayListener';

const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-');

interface ColorPickerProps {
  label: string;
  color: string;
  onChange: (color: string) => void;
}

const ColorPicker = ({ label, color = '#FFFFFF', onChange }: ColorPickerProps) => {
  const [isOpen, setOpen] = useState(false);
  const { mouseEventRef, hasClickedAway } = useClickAwayListener();
  const strikeThrough =
    'after:absolute after:left-0 after:top-0 after:w-[48px] after:border-b-red-500 after:border after:origin-bottom after:rotate-[-20deg] after:translate-y-2.5';

  const isCurrentColor = (hex: string) => hex.toLowerCase() === (color ?? '').toLowerCase();
  const currentPreset = PRESET_COLORS.find((preset) => isCurrentColor(preset.hex));
  const currentColorName = currentPreset ? capitalize(currentPreset.value) : (color ?? 'None');

  useEffect(() => {
    if (hasClickedAway) {
      setOpen(false);
    }
  }, [hasClickedAway]);

  return (
    <>
      <button
        type="button"
        aria-label={`${label}: ${currentColorName}`}
        aria-expanded={isOpen}
        data-testid={`civildraw-color-picker-trigger-${slugify(label)}`}
        className="block h-6 w-12 cursor-pointer appearance-none rounded border-0 p-0 shadow focus-ring"
        style={{
          backgroundColor: color,
        }}
        onClick={() => setOpen(true)}
      />
      {isOpen && (
        <div
          ref={mouseEventRef}
          className="position absolute z-50 min-w-[300px] rounded border border-gray-200 bg-white p-4 shadow"
        >
          <div className="pb-4 font-medium text-primary">{label}</div>
          <div className="grid grid-cols-5 gap-2 pb-4">
            {PRESET_COLORS.map((preset) => {
              if (preset.hex === 'transparent' && label !== 'Background' && label !== 'Border Color') return null;
              return (
                <Tooltip asChild key={preset.value} text={capitalize(preset.value)} sideOffset={8}>
                  <button
                    type="button"
                    aria-label={capitalize(preset.value)}
                    aria-pressed={isCurrentColor(preset.hex)}
                    data-testid={`civildraw-color-swatch-${slugify(preset.value)}`}
                    className={cn(
                      'relative block h-6 w-full cursor-pointer appearance-none rounded border border-gray-200 p-0 shadow outline-hidden hover:border-gray-400 focus-ring',
                      preset.hex === 'transparent' && strikeThrough,
                    )}
                    style={{ backgroundColor: preset.hex }}
                    onClick={() => onChange(preset.hex)}
                  />
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default ColorPicker;
